import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import {
  NotesFilterItemComponent,
  NotesScreenService,
  NoteVisitComponent,
  ProgressNoteFilterType,
  VisitGroup,
  VisitWithAdditionalFlags,
} from '@libs/notes';
import { TagEvent } from '@libs/shared-services';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { NoteListFiltersComponentService } from 'libs/notes/src/lib/components/note-list-filters/note-list-filters.component.service';
import { ProgressNoteStatus } from '@ffd/api-client';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, take } from 'rxjs';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { TooltipModule } from '@libs/ui-lib';
import { Actions, ofType } from '@ngrx/effects';
import { patientActions } from '@libs/store';
import { ToothDetailsView } from '../tooth-details/tooth-details.vm';
const perioRelatedDentalCodes = new Set(['D0180', 'D4341', 'D4342', 'D4910', 'D0120']);

// if the user is on charting view, we filter the visits to only include those that have progress notes for the given tooth position (or full mouth treatments).
// if the user is on perio view, we filter the visits to only include those that have progress notes with perio-related dental codes, and we don't show visits that only have clinical notes.
const filterVisits = (
  visits: VisitWithAdditionalFlags[],
  toothPosition: number,
  view: 'charting' | 'perio',
): VisitWithAdditionalFlags[] => {
  let filteredVisits: VisitWithAdditionalFlags[] = [];
  filteredVisits =
    view === 'charting'
      ? visits
          .map((visit) => ({
            ...visit,
            progressNotes: visit.progressNotes.filter((p) => p.positions.includes(toothPosition) || p.positions.length === 0),
          }))
          .filter((visit) => visit.progressNotes.length > 0 || visit.clinicalNotes.length > 0)
      : visits
          .map((visit) => ({
            ...visit,
            // Filter progress notes for perio-related dental codes
            progressNotes: visit.progressNotes.filter((p) => perioRelatedDentalCodes.has(p.dentalCode.code)),
          }))
          .filter((visit) => visit.progressNotes.length > 0);
  return filteredVisits;
};

@Component({
  selector: 'ffd-tooth-details-notes',
  templateUrl: './tooth-details-notes.component.html',
  styleUrls: ['./tooth-details-notes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SvgIconComponent, NoteVisitComponent, TranslateModule, NotesFilterItemComponent, LottieComponent, TooltipModule],
})
export class ToothDetailsNotesComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) public toothPosition!: number;
  @Input() public visits: VisitGroup[] = [];
  @Input() public visitsLoading: boolean = false;
  @Input() public isTimeZoneDifferentFromLocal: boolean = false;
  @Input() public siteId: string = '';
  @Input() public patientId: string = '';
  @Input() public view: ToothDetailsView = 'charting';
  @Input() public isNotesEmpty: boolean = false;
  @Input() public isCollapsed: boolean = false;
  @Input() public opacity: number = 1;
  @Output() public visitDateClick: EventEmitter<string> = new EventEmitter<string>();
  @Output() public hideConditionClick: EventEmitter<number> = new EventEmitter<number>();
  @Output() public deleteProgressNoteClick: EventEmitter<number> = new EventEmitter<number>();
  @Output() public collapseClick: EventEmitter<void> = new EventEmitter<void>();

  @ViewChildren(NoteVisitComponent, { read: ElementRef }) public noteVisitComponents?: QueryList<ElementRef<HTMLElement>>;

  private readonly filterService = inject(NoteListFiltersComponentService);
  private readonly notesScreenService = inject(NotesScreenService);
  private readonly actions$ = inject(Actions);
  public isFiltersVisible = false;

  protected readonly filterOptions = computed(() => this.filterService.viewModel().filterOptions);
  protected readonly activeFilters = computed(() => this.filterService.viewModel().activeFilters);
  protected readonly hasActiveFilters = computed(() => this.filterService.hasActiveFilters());
  protected readonly activeFiltersCount = computed(() => this.filterService.activeFiltersCount());

  protected readonly filterOptionsObservable$ = toObservable(this.filterOptions);
  public allVisits: VisitWithAdditionalFlags[] = [];
  public readonly lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  protected TagEvent = TagEvent;

  public ngOnInit(): void {
    //we need to store all visits here, so that we can reset them visits when the component is destroyed (this way don't need to re-fetch them)
    this.notesScreenService.visits$
      .pipe(
        filter((visits) => {
          return visits.length > 0;
        }),
        take(1),
      )
      .subscribe((visits) => {
        this.allVisits = visits;
        this.filterVisitsForToothPosition();
      });

    this.actions$
      .pipe(
        ofType(
          patientActions.onTreatmentStatusChange,
          patientActions.onTreatmentAdaCodeChange,
          patientActions.onTemporaryProgressNoteCreated,
        ),
      )
      .subscribe((change) => {
        // update this.allVisits
        queueMicrotask(() => {
          //update this.allVisits in a way that you only change 1 progress note in it (the one that status has changed)
          const allVisits = this.allVisits;
          if (change.type === patientActions.onTreatmentStatusChange.type) {
            const { progressNoteId, status } = change;
            this.allVisits = allVisits.map((visit) => ({
              ...visit,
              progressNotes: visit.progressNotes.map((note) => (note.id === progressNoteId ? { ...note, status: status } : note)),
            }));
          }
        });
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ((changes['toothPosition'] && !changes['toothPosition'].firstChange) || (changes['view'] && !changes['view'].firstChange)) {
      this.filterVisitsForToothPosition();
    }
  }

  public ngOnDestroy(): void {
    // Reset the visits

    this.notesScreenService.patchState({
      visits: this.allVisits,
      filteredVisits: this.allVisits,
    });
  }

  // We need to do some pre-filtering of visits based on the tooth position and view type, so that the note filters can work with the pre-filtered visits.
  public filterVisitsForToothPosition(): void {
    const filteredVisits = filterVisits(this.allVisits, this.toothPosition, this.view);
    this.notesScreenService.patchState({
      visits: filteredVisits,
      filteredVisits: filteredVisits,
    });
    this.filterService.resetFilters();
  }

  public clearFilters(): void {
    this.filterService.resetFilters();
  }

  public scrollToVisit(visitDate: string): void {
    if (this.noteVisitComponents) {
      const noteVisit = this.noteVisitComponents.find((noteVisitComponent) => {
        return noteVisitComponent.nativeElement.id === visitDate;
      });
      noteVisit?.nativeElement.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }

  public onVisitDateClick(visitId: string): void {
    this.visitDateClick.emit(visitId);
  }

  public onHideConditionClick(noteId: number): void {
    this.hideConditionClick.emit(noteId);
  }

  public onDeleteProgressNoteClick(noteId: number): void {
    this.deleteProgressNoteClick.emit(noteId);
  }

  public onFilterChange(type: ProgressNoteFilterType, value: (string | number | ProgressNoteStatus)[]): void {
    this.filterService.updateFilterOptionsAndFilteredVisits(value, type);
  }

  public toggleFilters(): void {
    this.isFiltersVisible = !this.isFiltersVisible;
  }

  public onCollapseClick(): void {
    this.collapseClick.emit();
  }
}
